import { useState, useEffect, useCallback, useRef } from 'react';

import { useSavePushSubscription } from 'src/Hooks/Mutations/useSavePushSubscription';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { userApi } from 'src/Services/API';
import { CreatePushSubscriptionDto } from 'src/Services/API/generated';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

// This helper function converts the VAPID public key from a URL-safe base64 string
// to a Uint8Array, which is required by the PushManager.subscribe() method.
function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  // State for the browser's notification permission status ('granted', 'denied', or 'default').
  const [permission, setPermission] = useState<NotificationPermission>('default');

  // State indicating if the currently logged-in user is subscribed on this device.
  // This is verified against our backend, not just the browser's local state.
  const [isSubscribed, setIsSubscribed] = useState(false);

  const { data: user } = useFetchUser();

  // A ref to prevent the initial synchronization useEffect from running twice in React's Strict Mode.
  const effectRan = useRef(false);

  const { mutate: saveSubscription, isLoading: isSavingSubscription } = useSavePushSubscription({
    onSuccess: () => {
      setIsSubscribed(true);
    },
    onError: error => {
      console.error('Failed to save push subscription:', error);
      setIsSubscribed(false);
    },
  });

  /**
   * Main function to request permission and create/save a subscription.
   * This is typically called by a user action (e.g., clicking "Enable Notifications").
   */
  const requestPermissionAndSubscribe = useCallback(async () => {
    if (!VAPID_PUBLIC_KEY) {
      console.error('VAPID public key is not defined.');
      return;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.error('Push Messaging is not supported');
      return;
    }

    // Trigger the native browser prompt for notification permission.
    const currentPermission = await Notification.requestPermission();
    setPermission(currentPermission);

    if (currentPermission !== 'granted') {
      return;
    }

    const registration = await navigator.serviceWorker.ready;

    try {
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true, // A requirement for web push.
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const subscriptionJSON = subscription.toJSON();

      if (
        !subscriptionJSON.endpoint ||
        !subscriptionJSON.keys?.p256dh ||
        !subscriptionJSON.keys?.auth
      ) {
        console.error(
          'Push subscription is missing one or more required properties (endpoint, p256dh, auth).'
        );
        return;
      }

      const payload: CreatePushSubscriptionDto = {
        endpoint: subscriptionJSON.endpoint,
        keys: {
          p256dh: subscriptionJSON.keys.p256dh,
          auth: subscriptionJSON.keys.auth,
        },
      };

      saveSubscription(payload);
    } catch (error) {
      console.error('Failed to subscribe the user: ', error);
    }
  }, [saveSubscription]);

  // This effect runs on mount to sync the browser's state with our application's state.
  useEffect(() => {
    // Strict Mode guard: ensures this effect runs only once on initial mount.
    if (effectRan.current === true) {
      return;
    }

    if (!user) return;

    async function syncSubscriptionState() {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return;
      }

      const browserPermission = Notification.permission;
      setPermission(browserPermission);

      if (browserPermission === 'denied') {
        setIsSubscribed(false);
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const localSubscription = await registration.pushManager.getSubscription();

      if (browserPermission === 'granted') {
        if (localSubscription) {
          // Edge Case 1: The browser has a subscription.
          // We must verify if it belongs to the *currently logged-in user*.
          // This handles the "shared computer" scenario where User B logs in after User A.
          const response = await userApi.userControllerCheckPushSubscription({
            endpoint: localSubscription.endpoint,
          });

          if (response.data.isSubscribedForCurrentUser) {
            setIsSubscribed(true);
          } else {
            // Edge Case 1b: Browser has a subscription but it's not in our DB.
            // This can happen if: subscriptions were deleted from DB, or VAPID keys changed.
            // If user had opted in, unsubscribe the old one and create a new subscription.
            setIsSubscribed(false);
            if (user?.transactionalPushOptIn) {
              console.log('Subscription not found in DB, re-subscribing...');
              await localSubscription.unsubscribe();
              await requestPermissionAndSubscribe();
            }
          }
        } else {
          // Edge Case 2: Permission is granted, but there's no browser subscription.
          // This happens if a user logs out (which calls unsubscribe) and then logs back in.
          setIsSubscribed(false);
          // We check the user's preference in our DB. If they had previously opted in,
          // we silently re-subscribe them without showing any new prompts.
          if (user?.transactionalPushOptIn) {
            await requestPermissionAndSubscribe();
          }
        }
      } else {
        // 'default' state: The user has not made a choice yet. They are not subscribed.
        // The PushNotificationProvider will handle prompting them.
        setIsSubscribed(false);
      }
    }

    syncSubscriptionState();

    return () => {
      effectRan.current = true;
    };
  }, [user, requestPermissionAndSubscribe]);

  return {
    permission,
    isSubscribed,
    requestPermissionAndSubscribe,
    isSavingSubscription,
  };
}
